import { useAtomValue } from 'jotai';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { userInfoAtom } from '../../../../../data/atoms/utilsAtoms';
import useClicks from '../../hooks/useClicks';
import Button from './button';
import Facebook from '../../../../../component/Facebook';
import { trackPlansPageEvent } from '@/data/utils/plansPageAnalytics';
import { isMatchingCurrentPlan } from '@/data/utils/subscriptionPlanMatch';

const ChargeBeeButton = ({
	isBestValue = false,
	planId,
	planCbItem,
	hasSeatSelection = false,
	onOpenCheckout,
	activePlan,
	currentSubscriptionDetails,
	currentSubscriptionLoading = false
}) => {
	const userData = useAtomValue(userInfoAtom);
	const navigate = useNavigate();
	const { handleStartTrial, handleStartSubscription } = useClicks(
		userData,
		planId,
		!userData.subscriptionId,
		planCbItem
	);

	const [hasPurchasedSubscription, setHasPurchasedSubscription] = React.useState(null);
	const [checkingChargebee, setCheckingChargebee] = React.useState(false);

	React.useEffect(() => {
		if (!userData.user?.email || userData.subscriptionId) return;

		const checkChargebeeSubscriptionHistory = async () => {
			try {
				setCheckingChargebee(true);
				const response = await fetch('/api/chargebee/check-customer-exists', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ email: userData.user.email })
				});

				const data = await response.json();
				setHasPurchasedSubscription(data.hasPurchasedSubscription);
			} catch (error) {
				console.error('Error checking Chargebee subscription history:', error);
				setHasPurchasedSubscription(false);
			} finally {
				setCheckingChargebee(false);
			}
		};

		checkChargebeeSubscriptionHistory();
	}, [userData.user?.email, userData.subscriptionId]);

	const isCurrentPlan = userData.subscriptionId
		? isMatchingCurrentPlan({
				currentSubscriptionDetails,
				plan: { id: planId, cbItem: planCbItem, hasSeatSelection },
				activePlan
			})
		: false;

	const hasActiveSubscription = Boolean(userData.subscriptionId);
	const isReturningSubscriber =
		!hasActiveSubscription && (userData.user?.isTrialAvailed === true || hasPurchasedSubscription === true);
	const isCancelledSubscription =
		isReturningSubscriber &&
		(currentSubscriptionDetails?.status === 'cancelled' ||
			userData.user?.subscription === false ||
			userData.user?.isTrialAvailed === true ||
			hasPurchasedSubscription === true);

	const getButtonText = () => {
		const userType = userData.user?.userType;

		if (isCancelledSubscription) {
			return 'Select Plan';
		}

		if (!userData.subscriptionId && !planId.startsWith('STUDIO')) {
			return 'START FREE TRIAL';
		}

		if (!userData.subscriptionId && planId.startsWith('STUDIO')) {
			return 'GET STARTED';
		}

		if (isCurrentPlan) {
			return 'Current';
		}

		if (
			(userType === 'studio-owner' && (planId.startsWith('INDIVIDUAL') || planId.startsWith('VIP'))) ||
			(userType === 'vip' && planId.startsWith('INDIVIDUAL'))
		) {
			return 'Downgrade';
		}

		return 'Upgrade';
	};

	const handleUpgradeDowngrade = () => {
		trackPlansPageEvent({
			eventName: 'upgrade_downgrade_clicked',
			planId: planCbItem || planId,
			billingCycle: planId.includes('Monthly') ? 'monthly' : 'yearly',
			source: 'plans_page_pricing_card'
		});
		if (onOpenCheckout && activePlan) {
			onOpenCheckout({ open: true, plan: activePlan });
		}
	};

	const defaultTitle = userData.user?.isTrialAvailed
		? 'Manage Subscription'
		: hasSeatSelection
			? 'Get Started'
			: 'Start Free Trial';

	const getButtonStyle = () => {
		const buttonText = getButtonText();

		if (
			buttonText === 'START FREE TRIAL' ||
			buttonText === 'GET STARTED' ||
			buttonText === 'Select Plan' ||
			!userData.subscriptionId
		) {
			return {
				color: 'white',
				borderColor: 'white',
				borderWidth: '0.0625rem',
				borderStyle: 'solid',
				background: '#05729b'
			};
		}

		if (buttonText === 'Upgrade' || !userData.subscriptionId) {
			return {
				color: 'white',
				borderColor: 'white',
				borderWidth: '0.0625rem',
				borderStyle: 'solid'
			};
		} else {
			return {
				color: 'white',
				borderColor: 'white',
				borderWidth: '0.0625rem',
				borderStyle: 'solid'
			};
		}
	};

	const isUpgradeButton = () => {
		return getButtonText() === 'Upgrade';
	};

	const handleButtonClick = () => {
		const billingCycle = planId.includes('Monthly') ? 'monthly' : 'yearly';
		const planType = planId.startsWith('STUDIO') ? 'studio' : planId.startsWith('VIP') ? 'vip' : 'standard';
		const onboardingCompleted = userData.user?.onboardingCompleted;

		trackPlansPageEvent({
			eventName: 'plan_button_clicked',
			planId: planCbItem || planId,
			billingCycle,
			source: 'plans_page_pricing_card',
			meta: { planType, buttonText: getButtonText() }
		});

		if (isCancelledSubscription) {
			handleUpgradeDowngrade();
			return;
		}

		if (!userData.subscriptionId && !onboardingCompleted) {
			const onboardingState = {
				profileCreated: true,
				selectedPlanId: planId,
				selectedPlanType: planType,
				selectedPlanCbItem: planCbItem,
				selectedBillingCycle: billingCycle,
				currentStep: 2
			};
			sessionStorage.setItem('onboarding_state', JSON.stringify(onboardingState));

			const params = new URLSearchParams({
				plan: planType,
				billing: billingCycle,
				planId: planId
			});
			navigate(`/onboarding/checkout?${params.toString()}`, { state: { from: '/plans' } });
		} else if (userData.subscriptionId) {
			handleStartTrial();
		} else {
			handleStartSubscription();
		}
	};

	if (!userData.token || !userData.user?.email) {
		const handleNonLoggedInClick = () => {
			const billingCycle = planId.includes('Monthly') ? 'monthly' : 'yearly';
			const planType = planId.startsWith('STUDIO') ? 'studio' : planId.startsWith('VIP') ? 'vip' : 'standard';

			trackPlansPageEvent({
				eventName: 'non_logged_in_plan_click',
				planId: planCbItem || planId,
				billingCycle,
				source: 'plans_page_pricing_card',
				meta: { planType }
			});

			const onboardingState = {
				selectedPlanId: planId,
				selectedPlanType: planType,
				selectedPlanCbItem: planCbItem,
				selectedBillingCycle: billingCycle,
				currentStep: 0
			};
			sessionStorage.setItem('onboarding_state', JSON.stringify(onboardingState));

			const params = new URLSearchParams({
				plan: planType,
				billing: billingCycle,
				planId: planId
			});
			navigate(`/onboarding/create-profile?${params.toString()}`, { state: { from: '/plans' } });
		};

		return (
			<>
				<Button onClick={handleNonLoggedInClick} isBestValue={isBestValue}>
					{defaultTitle}
				</Button>
			</>
		);
	}

	const buttonStyle = getButtonStyle();
	const onboardingCompleted = userData.user?.onboardingCompleted;
	const showStatusLoader = Boolean(userData.subscriptionId) && currentSubscriptionLoading;

	// Show loader while checking Chargebee for non-subscribed users
	const shouldShowChargebeeLoader = !userData.subscriptionId && checkingChargebee;

	const buttonContent = shouldShowChargebeeLoader ? (
		<span className="cta-button__status">
			<span className="cta-button__spinner" aria-hidden="true" />
			<span>Loading...</span>
		</span>
	) : showStatusLoader ? (
		<span className="cta-button__status">
			<span className="cta-button__spinner" aria-hidden="true" />
			<span>Loading...</span>
		</span>
	) : (
		getButtonText()
	);

	return (
		<>
			<Facebook />
			{userData.subscriptionId && !isCancelledSubscription ? (
				<Button
					onClick={() => {
						trackPlansPageEvent({
							eventName: 'subscription_button_clicked',
							planId: planCbItem || planId,
							billingCycle: planId.includes('Monthly') ? 'monthly' : 'yearly',
							source: 'plans_page_pricing_card',
							meta: { buttonText: getButtonText() }
						});
						getButtonText() === 'Upgrade' || getButtonText() === 'Downgrade' || getButtonText() === 'Select Plan'
							? handleUpgradeDowngrade()
							: handleStartTrial();
					}}
					isBestValue={isUpgradeButton() || !userData.subscriptionId}
					style={buttonStyle}
					disabled={showStatusLoader}
				>
					{buttonContent}
				</Button>
			) : isCancelledSubscription ? (
				<Button
					key={planCbItem ?? planId}
					onClick={handleUpgradeDowngrade}
					isBestValue={isBestValue}
					style={buttonStyle}
					disabled={shouldShowChargebeeLoader}
				>
					{buttonContent}
				</Button>
			) : onboardingCompleted ? (
				<Button
					key={planCbItem ?? planId}
					dataCbType={'checkout'}
					dataCbItem={planCbItem}
					onClick={() => {
						trackPlansPageEvent({
							eventName: 'onboarding_completed_plan_click',
							planId: planCbItem || planId,
							billingCycle: planId.includes('Monthly') ? 'monthly' : 'yearly',
							source: 'plans_page_pricing_card'
						});
						handleStartSubscription();
					}}
					isBestValue={isUpgradeButton() || !userData.subscriptionId}
					style={buttonStyle}
					disabled={shouldShowChargebeeLoader}
				>
					{buttonContent}
				</Button>
			) : (
				<Button
					key={planCbItem ?? planId}
					onClick={handleButtonClick}
					isBestValue={isUpgradeButton() || !userData.subscriptionId}
					style={buttonStyle}
					disabled={shouldShowChargebeeLoader}
				>
					{buttonContent}
				</Button>
			)}
		</>
	);
};

export default ChargeBeeButton;
